import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { HorizontalCard } from '../../partials/cards/horizontalCard/HorizontalCard';
import { VerticalCard } from '../../partials/cards/verticalCard/VerticalCard';
import { useTheme } from '../../../theming/useTheme';
import { VrLineHeight } from '../../../theming/verticalRhythm/verticalRhythm';
import { CardComponentDataProps } from '../../partials/cards/dividedCard/DividedCard';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';

const componentConfigThemeKey = 'oneThreeArticleList';
const createStyle = (vrLineHeight: VrLineHeight) => StyleSheet.create({
  container: {
    display: 'grid',
    gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
    gridTemplateRows: '1fr',
    gridTemplateColumns: '1fr 1fr',
    [MEDIA_BREAKPOINTS.SMALL]: {
      gridColumnGap: '0',
      gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
      gridTemplateRows: 'auto',
      gridTemplateColumns: '1fr',
    },
  },
  horizontalCardsWrapper: {
    position: 'relative',
  },
  horizontalCardsContainer: {
    maxHeight: '100%',
    height: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: '1fr 1fr 1fr',
    [MEDIA_BREAKPOINTS.LARGE]: {
      gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`,
    },
  },
  horizontalCardWrapper: {
    minWidth: '0',
    minHeight: '0',
    boxSizing: 'border-box',
    padding: '0.1px',
    position: 'relative',
    ':last-child': {
      marginBottom: '0',
    },
  },
});

interface OneThreeArticleListComponentDataProps {
  cardsProps: CardComponentDataProps[]
}

interface OneThreeArticleListComponentThemeProps {
  vrLineHeight: VrLineHeight
}

type OneThreeArticleListComponentProps = OneThreeArticleListComponentDataProps & OneThreeArticleListComponentThemeProps;

export const OneThreeArticleListComponent: React.FunctionComponent<OneThreeArticleListComponentProps> = ({ cardsProps, vrLineHeight }) => {
  const [highlight, ...moreCards] = cardsProps;
  const style = createStyle(vrLineHeight);
  const createCard = (cardProps: CardComponentDataProps, i: number) => (
    <div className={css(style.horizontalCardWrapper)} key={i}>
      <HorizontalCard {...cardProps} />
    </div>
  );

  return (
    <div className={css(style.container)}>
      <VerticalCard {...highlight} />
      <div className={css(style.horizontalCardsWrapper)}>
        <div className={css(style.horizontalCardsContainer)}>
          {moreCards.map(createCard)}
        </div>
      </div>
    </div>
  );
};

OneThreeArticleListComponent.defaultProps = {
  cardsProps: [],
};

const mapThemeToProps = (theme: OneThreeArticleListComponentThemeProps) => ({
  vrLineHeight: theme.vrLineHeight,
});

export const OneThreeArticleList = (props: OneThreeArticleListComponentDataProps) => {
  const themeContext = useTheme(mapThemeToProps, componentConfigThemeKey);
  return <ErrorBoundary><OneThreeArticleListComponent {...themeContext} {...props} /></ErrorBoundary>;
};
